import React, {useEffect} from 'react';
import {DevSettings, Platform, StyleSheet, View} from 'react-native';
import {initialWindowMetrics, SafeAreaProvider} from 'react-native-safe-area-context';
import {getStateFromPath, InitialState} from '@react-navigation/native';
import {createStackNavigator, StackNavigationOptions} from '@react-navigation/stack';
import {ThemeProvider} from '@rneui/themed';
import {QueryClientProvider} from '@tanstack/react-query';
import {observer} from 'mobx-react-lite';

// Development-only React Query DevTools (tree-shaken in production)
const ReactQueryDevtools = __DEV__
  ? require('@tanstack/react-query-devtools').ReactQueryDevtools
  : null;

import AppNavigationContainer from './app/components/AppNavigationContainer';
import ErrorBoundary from './app/components/ErrorBoundary';
import {GlobalAlertBanner, SilentErrorBoundary} from './app/components/GlobalAlert';
import ToastBottom from './app/components/ToastBottom';
import config from './app/config';
import {useWebScreenDimensions} from './app/hooks/useWebScreenDimensions';
import {AppRoute} from './app/models/AppRoute';
import {RootStackParamList} from './app/models/navigationTypes';
import DrawerNavigator from './app/navigation/DrawerNavigator';
import {navStructure} from './app/navigation/linking';
import RootCheckoutFlowStackNavigator from './app/navigation/RootCheckoutFlowNavigator';
import {navigate} from './app/navigation/RootNavigation';
import RootPromotionsFlowStackNavigator from './app/navigation/RootPromotionsFlowStackNavigator';
import RootRewardsFlowStackNavigator from './app/navigation/RootRewardsFlowStackNavigator';
import RootStoreSelectionFlowStackNavigator from './app/navigation/RootStoreSelectionFlowStackNavigator';
import RootStorybookStackNavigator from './app/navigation/RootStorybookStackNavigator';
import {shouldDetachInactiveScreens} from './app/navigation/util';
import {queryClient} from './app/queries/queryClient';
import AppBuildInfoScreen from './app/screens/AppBuildInfoScreen';
import ForceUpgradeScreen from './app/screens/ForceUpgradeScreen';
import SummerCashGrabScreen from './app/screens/games/SummerCashGrabScreen';
import SplashScreen from './app/screens/SplashScreen';
import {useStores} from './app/stores/use-stores';
import {reactNativeElementsTheme} from './app/style/reactNativeElementsTheme';
import {initialiseOneSignal} from './app/utils/onesignal';
import {TraceProvider} from './app/utils/telemetry';

const RootStack = createStackNavigator<RootStackParamList>();

// Native Storybook entry point. Gated by environment only, never by store type or login state.
if (__DEV__ && config.STORYBOOK_ENABLED && Platform.OS !== 'web') {
  DevSettings.addMenuItem('Storybook', () => navigate(AppRoute.ROOT_STORYBOOK));
}

const App = observer(() => {
  const {globalStore, initialiseApp, shopSelectionStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const [initialState, setInitialState] = React.useState<InitialState | undefined>(undefined);
  // 0 while a store switch is in flight, so the shell's first paint after the
  // splash is invisible and the opacity transition can run. Native stays at 1.
  const [storeReveal, setStoreReveal] = React.useState(1);
  if (Platform.OS === 'web' && globalStore.isSwitchingStore && storeReveal !== 0) {
    setStoreReveal(0);
  }

  React.useEffect(() => {
    if (Platform.OS !== 'web' || globalStore.isSwitchingStore || storeReveal !== 0) return;
    // Two frames: the first commits opacity 0, the second starts the fade.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setStoreReveal(1));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [globalStore.isSwitchingStore, storeReveal]);

  useEffect(() => {
    async function initialise() {
      await initialiseOneSignal(config);
      await initialiseApp();
    }
    initialise(); // only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (globalStore.isAppInitialising || globalStore.isSwitchingStore) {
    return <SplashScreen />;
  }

  // Block app access if upgrade is required
  if (globalStore.requiresUpgrade) {
    return <ForceUpgradeScreen />;
  }

  const selectedStore = shopSelectionStore?.selectedStore;
  const isStoreSelected = selectedStore?.id;

  // if the app was open through a link and a store was not selected, we
  // need to delay the original navigation until the store is selected
  if (
    isStoreSelected &&
    !globalStore.isAppInitialising &&
    !globalStore.isSwitchingStore &&
    !globalStore.wasStoreSelectedOnAppStart
  ) {
    if (globalStore.delayedNavigationPath) {
      const delayedInitialState = getStateFromPath(globalStore.delayedNavigationPath, navStructure);

      setInitialState(delayedInitialState);
    }
    globalStore.setWasStoreSelectedOnAppStart(true);
  }

  const options: StackNavigationOptions = {headerShown: false};

  return (
    <View style={styles.main} testID="appRoot">
      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <TraceProvider>
              <ThemeProvider theme={reactNativeElementsTheme}>
                <SilentErrorBoundary>
                  <GlobalAlertBanner />
                </SilentErrorBoundary>
                <View
                  style={[
                    styles.navigatorArea,
                    Platform.OS === 'web'
                      ? ({
                          opacity: storeReveal,
                          transitionProperty: 'opacity',
                          transitionDuration: '200ms',
                          transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
                        } as View['props']['style'])
                      : null,
                  ]}>
                  <AppNavigationContainer initialState={initialState}>
                    <RootStack.Navigator detachInactiveScreens={shouldDetachInactiveScreens()}>
                      {isStoreSelected ||
                      globalStore.isAccountRoute ||
                      globalStore.isInformationRoute ? (
                        <RootStack.Screen
                          name={AppRoute.ROOT}
                          component={DrawerNavigator}
                          options={{...options, title: 'Back'}}
                          getId={() => AppRoute.ROOT}
                        />
                      ) : (
                        <RootStack.Screen
                          name={AppRoute.ROOT_STORE_SELECTION_FLOW}
                          component={RootStoreSelectionFlowStackNavigator}
                          options={options}
                          getId={() => AppRoute.ROOT_STORE_SELECTION_FLOW}
                        />
                      )}
                      <RootStack.Screen
                        name={AppRoute.ROOT_REWARDS_FLOW}
                        component={RootRewardsFlowStackNavigator}
                        options={options}
                        getId={() => AppRoute.ROOT_REWARDS_FLOW}
                      />
                      <RootStack.Screen
                        name={AppRoute.ROOT_CHECKOUT_FLOW}
                        component={RootCheckoutFlowStackNavigator}
                        options={options}
                        getId={() => AppRoute.ROOT_CHECKOUT_FLOW}
                      />
                      <RootStack.Screen
                        name={AppRoute.ROOT_PROMOTIONS_FLOW}
                        component={RootPromotionsFlowStackNavigator}
                        options={options}
                        getId={() => AppRoute.ROOT_PROMOTIONS_FLOW}
                      />
                      {config.STORYBOOK_ENABLED ? (
                        <RootStack.Screen
                          name={AppRoute.ROOT_STORYBOOK}
                          component={RootStorybookStackNavigator}
                          options={options}
                          getId={() => AppRoute.ROOT_STORYBOOK}
                        />
                      ) : null}
                      <RootStack.Screen
                        name={AppRoute.ROOT_APP_BUILD_INFO}
                        component={AppBuildInfoScreen}
                        options={options}
                        getId={() => AppRoute.ROOT_APP_BUILD_INFO}
                      />
                      <RootStack.Group screenOptions={{presentation: 'modal'}}>
                        <RootStack.Screen
                          name={AppRoute.SUMMER_CASH_GRAB_GAME}
                          component={SummerCashGrabScreen}
                          options={{
                            title: 'Summer Cash Grab Game',
                          }}
                          getId={() => AppRoute.SUMMER_CASH_GRAB_GAME}
                        />
                      </RootStack.Group>
                    </RootStack.Navigator>
                    <ToastBottom />

                    {/* React Query DevTools - Web desktop only (hidden on mobile viewports) */}
                    {__DEV__ &&
                    Platform.OS === 'web' &&
                    isWebDesktopScreen &&
                    ReactQueryDevtools ? (
                      <ReactQueryDevtools initialIsOpen={false} />
                    ) : null}
                  </AppNavigationContainer>
                </View>
              </ThemeProvider>
            </TraceProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </SafeAreaProvider>
    </View>
  );
});

// Export app normally - Storybook opens from the dev menu (native) or the drawer (web) when STORYBOOK_ENABLED is true
export default App;

const styles = StyleSheet.create({
  main: {
    flex: 1,
    height: '100%',
  },
  // Fills the remaining column height beneath the GlobalAlert banner so the
  // banner pushes navigator content down instead of overlaying it.
  navigatorArea: {
    flex: 1,
  },
});
